import React, { useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { Box, Typography, Tooltip } from '@mui/material'; // Import Box, Typography, and Tooltip from Material-UI
import { Link } from 'react-router-dom'; // Import Link from react-router-dom
import Pagination from '@mui/material/Pagination'; // Import Pagination from Material-UI
import { formatDate } from '../utils/formatterUtils'; // Import formatDate
import config from '../config/config'; // Import configuration

// Fix for default icon issue with Leaflet and Webpack
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png',
});

const getStatusColor = (status) => {
  switch (status) {
    case 'Ongoing':
      return 'yellow';
    case 'Completed':
      return 'green';
    case 'Delayed':
      return 'red';
    default:
      return 'gray';
  }
};

const StatusCircle = ({ status, onClick, onMouseEnter, onMouseLeave }) => (
  <Tooltip title={status} arrow>
    <span
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      style={{
        display: 'inline-block',
        width: '12px',
        height: '12px',
        borderRadius: '50%',
        backgroundColor: getStatusColor(status),
        marginRight: '8px',
        cursor: 'pointer',
      }}
    ></span>
  </Tooltip>
);

const Map = ({ projects }) => {
  const [filteredStatus, setFilteredStatus] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const projectsPerPage = 5;

  const handleMarkerClick = (location) => {
    setFilteredStatus(null);
    setCurrentPage(1);
  };

  const handleStatusClick = (status) => {
    setFilteredStatus(status);
    setCurrentPage(1);
  };

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  const getProjectsByLocation = (location) => {
    return projects.filter(project => project.location_id && project.location_id.locationName === location);
  };

  const getFilteredProjects = (projects) => {
    if (filteredStatus) {
      return projects.filter(project => project.status === filteredStatus);
    }
    return projects;
  };

  const paginatedProjects = (projects) => {
    const startIndex = (currentPage - 1) * projectsPerPage;
    return projects.slice(startIndex, startIndex + projectsPerPage);
  };

  // Group projects by location
  const projectsByLocation = projects.reduce((acc, project) => {
    if (project.location_id) {
      const locationKey = `${project.location_id.latitude},${project.location_id.longitude}`;
      if (!acc[locationKey]) {
        acc[locationKey] = [];
      }
      acc[locationKey].push(project);
    }
    return acc;
  }, {});

  return (
    <MapContainer center={[0, 0]} zoom={2} style={{ height: '100vh', width: '100%' }}>  <TileLayer
        url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
      />
      {Object.keys(projectsByLocation).map((locationKey) => {
        const [latitude, longitude] = locationKey.split(',').map(Number);
        const locationProjects = projectsByLocation[locationKey];
        return (
          <Marker key={locationKey} position={[latitude, longitude]} eventHandlers={{ click: () => handleMarkerClick(locationProjects[0].location_id.locationName) }}>
            <Popup>
              <Typography variant="h6">{locationProjects[0].location_id.locationName}</Typography>
              <ul>
                {paginatedProjects(getFilteredProjects(getProjectsByLocation(locationProjects[0].location_id.locationName))).map((project) => (
                  <li key={project.project_id}>
                    <Box display="flex" alignItems="center">
                      <StatusCircle status={project.status} />
                      <Link to={`/project-edit/${project.project_id}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                        <strong>{project.project_name}</strong>
                      </Link>
                    </Box>
                    Estimated Completion Date: {formatDate(project.planned_completion_date, config.defaultLocale)}
                  </li>
                ))}
              </ul>
              <Box display="flex" justifyContent="space-between" alignItems="center" mt={2}>
                <Pagination
                  count={Math.ceil(getFilteredProjects(getProjectsByLocation(locationProjects[0].location_id.locationName)).length / projectsPerPage)}
                  page={currentPage}
                  onChange={handlePageChange}
                  size="small"
                />
                <Box display="flex">
                  <StatusCircle status="Ongoing" onClick={() => handleStatusClick('Ongoing')} />
                  <StatusCircle status="Completed" onClick={() => handleStatusClick('Completed')} />
                  <StatusCircle status="Delayed" onClick={() => handleStatusClick('Delayed')} />
                </Box>
              </Box>
            </Popup>
          </Marker>
        );
      })}
    </MapContainer>
  );
};

export default Map;